import React from 'react';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {categoriaDellaHome, fasciaFornitori, lobbyCasino, loghiFornitori, tuttiIGiochiDellaScheda} from '../../moduli/leggi';
import {useSkin} from '../../moduli/useSkin';
import {inAnteprima} from '../../skin';
import {giochi as giochiCasino, descriviErrore as erroreCasino} from '../../services/casinoApi';
import Caricamento from '../common/Caricamento';
import Copertina from '../Casino/Copertina';
import FasciaFornitori from '../Casino/FasciaFornitori';
import CassettoFornitori, {TastoFornitori} from '../Casino/CassettoFornitori';
import {logoFornitore} from '../Casino/fornitoriNoti';
import Pagine, {usePagine, useRicordaScorrimento} from '../Casino/Pagine';
import {RigaSegnaposto} from '../Casino/Scheletro';
import {percorsoSezione} from '../Casino/comune';
import Nota from './Nota';
import '../Casino/Casino.css';
import './Vetrina.css';

/**
 * LA PAGINA DI UNA CATEGORIA SOLO DELLA HOME (/categoria/scelti-per-te): ci
 * si arriva dal nome della riga in Home o dal suo «Vedi tutti». Mostra TUTTI
 * i giochi della categoria, non solo i primi `quanti` della riga,
 * nell'ordine scelto nel backoffice (Moduli › Home › Righe di giochi), a
 * griglia e a pagine (modulo Giochi per pagina, di serie 30).
 *
 * Le schede di Casino' e Live casino una pagina ce l'hanno gia': la lobby
 * sulla scheda (/casino?scheda=top10). Questa e' per le categorie che stanno
 * solo nella Home, coi giochi di Casino' e Live casino insieme: ogni gioco
 * si apre nella sua sezione, come dalla riga (da scollegati la pagina del
 * gioco apre la finestra di accesso).
 *
 * La categoria si legge dalle righe della Home (categoriaDellaHome), con le
 * stesse regole: spenta, senza nome, col blocco spento o mai scritta, la
 * pagina lo dice e porta alla Home. Niente ricerca o preferiti: e' la riga
 * per intero, non una lobby. Le classi sono quelle della lobby.
 *
 * In cima, come nella lobby, la FASCIA DEI FORNITORI che hanno giochi in
 * questa categoria (FasciaFornitori.js): toccandone uno si filtra la pagina
 * (?fornitore=). Segue il modulo Casinò: filtro per fornitore acceso e «In
 * cima alla lobby» su Fornitori. Fornitore e pagina stanno nell'indirizzo,
 * cosi' tornando da un gioco si ritrova la stessa pagina allo stesso punto.
 */
export default function PaginaCategoria() {
  useSkin();
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {id} = useParams();
  const riga = categoriaDellaHome(id);
  const scheda = riga ? riga.scheda : null;

  /* Gli elenchi delle sezioni da cui la categoria prende i giochi, come le righe della Home. */
  const chiave = scheda ? scheda.servono.join(',') : '';
  const [stato, setStato] = React.useState({carica: true, errore: '', elenchi: {}});
  React.useEffect(() => {
    let vivo = true;
    setStato({carica: true, errore: '', elenchi: {}});
    Promise.all(chiave.split(',').filter(Boolean).map((k) => giochiCasino(k).then((elenco) => [k, elenco])))
        .then((coppie) => vivo && setStato({carica: false, errore: '', elenchi: Object.fromEntries(coppie)}))
        .catch((e) => vivo && setStato({carica: false, errore: erroreCasino(e), elenchi: {}}));
    return () => { vivo = false; };
  }, [chiave]);

  const elenchi = (scheda ? scheda.servono : []).map((k) => [k, stato.elenchi[k] || []]);
  const tutti = tuttiIGiochiDellaScheda(scheda, elenchi.flatMap(([, elenco]) => elenco));
  /* La fascia dei fornitori della categoria (non di tutto il catalogo). */
  const modulo = lobbyCasino('casino');
  const conFascia = modulo.fornitori && modulo.inCima === 'fornitori';
  const [parametri, setParametri] = useSearchParams();
  const fornitore = conFascia ? (parametri.get('fornitore') || '') : '';
  const scegli = (f) => setParametri((prima) => {
    const p = new URLSearchParams(prima);
    if (f) p.set('fornitore', f); else p.delete('fornitore');
    p.delete('pagina');
    return p;
  }, {replace: true});
  const loghi = loghiFornitori();
  const nomi = Array.from(new Set(tutti.map((g) => g.fornitore).filter(Boolean).concat(fornitore ? [fornitore] : [])))
      .sort((a, b) => a.localeCompare(b));
  const voci = nomi.map((nome) => ({nome, immagine: logoFornitore(nome, loghi)}));
  /* Sul telefono la fascia non c'e': il cassetto si apre dal tasto accanto al titolo. */
  const [cassetto, setCassetto] = React.useState(false);
  const chiudiCassetto = React.useCallback(() => setCassetto(false), []);
  React.useEffect(() => { setCassetto(false); }, [id]);
  const giochi = fornitore ? tutti.filter((g) => g.fornitore === fornitore) : tutti;
  /* Cambiando categoria o fornitore si riparte dalla prima pagina. */
  const pagine = usePagine(giochi, id + '|' + fornitore);
  useRicordaScorrimento(!stato.carica);

  /* Ogni gioco si apre nella sua sezione: quella dell'elenco da cui arriva. */
  const apri = (g) => {
    const k = (elenchi.find(([, elenco]) => elenco.includes(g)) || ['casino'])[0];
    navigate(percorsoSezione(k) + '/gioco/' + encodeURIComponent(g.id));
  };

  return (
    <div className={'cas-lobby cas-medie'}>
      <div className={'cas-intesta'}>
        <h1>{scheda ? (scheda.icona ? scheda.icona + ' ' : '') + scheda.nome : t('Categoria')}</h1>
        {scheda && conFascia && !stato.carica && voci.length > 1 && (
          <TastoFornitori quanti={voci.length} scelto={fornitore} aperto={cassetto} onApri={() => setCassetto((a) => !a)}/>
        )}
      </div>
      {!scheda && (
        <>
          <Nota tono={'spento'} giocatore={t('Questa categoria non è disponibile.')}>
            {t("Questa categoria non c'è o è spenta: le categorie della Home si scelgono dal backoffice (Moduli › Home › Righe di giochi).")}
          </Nota>
          <button type={'button'} className={'cas-tutti'} onClick={() => navigate('/')}>{t('Torna alla Home')}</button>
        </>
      )}
      {scheda && conFascia && !stato.carica && voci.length > 1 && (
        <>
          <FasciaFornitori voci={voci} scelto={fornitore} etichetta={t('Fornitori')} marquee {...fasciaFornitori()}
            onScegli={(v) => scegli(v.nome === fornitore ? '' : v.nome)}/>
          <CassettoFornitori aperto={cassetto} onChiudi={chiudiCassetto} voci={voci} scelto={fornitore} titolo={t('Fornitori')}
            onScegli={(v) => scegli(v.nome)}/>
        </>
      )}
      {scheda && stato.carica && <Caricamento forma={'righe'} righe={4} testo={t('Carico i giochi')}/>}
      {scheda && stato.errore && (
        <Nota tono={'errore'}>{stato.errore}</Nota>
      )}
      {scheda && !stato.carica && !stato.errore && !tutti.length && (
        <Nota tono={'spento'} giocatore={t('Non ci sono ancora giochi in questa categoria.')}>
          {t('La categoria «{{nome}}» non ha ancora giochi: si scelgono dal backoffice (Moduli › Home › Righe di giochi).', {nome: scheda.nome})}
        </Nota>
      )}
      {scheda && !stato.carica && !tutti.length && inAnteprima() && <RigaSegnaposto quanti={6}/>}
      {scheda && !stato.carica && tutti.length > 0 && !giochi.length && <Nota>{t('Nessun gioco trovato.')}</Nota>}
      {giochi.length > 0 && (
        <>
          <div className={'cas-griglia'}>
            {pagine.visibili.map((g) => <Copertina key={g.id} gioco={g} onApri={apri}/>)}
          </div>
          <Pagine {...pagine}/>
        </>
      )}
    </div>
  );
}
